import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Plus, Trash2, Upload, X } from "lucide-react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "../../components/ui/Button";
import { Field } from "../../components/ui/Field";
import { FormCard } from "../../components/ui/FormCard";
import { PageTitle } from "../../components/ui/PageTitle";
import { api } from "../../lib/api";

const WEIGHT_UNITS = ["GM", "KG", "ML", "L", "PCS"];

const schema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  stock: z.number().int().min(0).optional(),
  threshold: z.number().int().min(0).optional(),
  tags: z.string().optional(),
  status: z.enum(["Active", "Inactive"]),
  subtitle: z.string().optional(),
  origin: z.string().optional(),
  highlights: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;
type ImageItem = { url: string; filename: string; mimetype: string };
type WeightOption = {
  weightValue: string;
  weightUnit: string;
  price: string;
  comparePrice: string;
};
type ProductImageResponse = {
  url: string;
  filename?: string;
  mimetype?: string;
};
type ProductWeightResponse = {
  weightValue: number | string;
  weightUnit: string;
  price: number | string;
  comparePrice?: number | string | null;
};
type ProductResponse = {
  name?: string;
  description?: string;
  stock?: number;
  threshold?: number;
  tags?: string;
  status?: "Active" | "Inactive";
  subtitle?: string;
  origin?: string;
  highlights?: string[];
  images?: ProductImageResponse[];
  weightOptions?: ProductWeightResponse[];
};
type UploadResponse = { url: string; filename: string; mimetype: string };

const emptyWeight = (): WeightOption => ({
  weightValue: "",
  weightUnit: "GM",
  price: "",
  comparePrice: "",
});

export function ProductFormPage({ mode }: { mode: "new" | "edit" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(mode === "edit");
  const [error, setError] = useState("");
  const [weightOptions, setWeightOptions] = useState<WeightOption[]>([
    emptyWeight(),
  ]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    defaultValues: { status: "Active", stock: 0, threshold: 0 },
  });

  useEffect(() => {
    if (mode !== "edit" || !id) return;
    api
      .get(`/admin/products/${id}`)
      .then(({ data }) => {
        const p = data.product as ProductResponse;
        form.reset({
          name: p.name,
          description: p.description || "",
          stock: p.stock,
          threshold: p.threshold,
          tags: p.tags || "",
          status: p.status,
          subtitle: p.subtitle || "",
          origin: p.origin || "",
          highlights: Array.isArray(p.highlights)
            ? p.highlights.join("\n")
            : "",
        });
        setImages(
          (p.images ?? []).map((img) => ({
            url: img.url,
            filename: img.filename || "",
            mimetype: img.mimetype || "image/jpeg",
          })),
        );
        setWeightOptions(
          p.weightOptions && p.weightOptions.length > 0
            ? p.weightOptions.map((w) => ({
                weightValue: String(w.weightValue),
                weightUnit: w.weightUnit,
                price: String(w.price),
                comparePrice:
                  w.comparePrice != null ? String(w.comparePrice) : "",
              }))
            : [emptyWeight()],
        );
      })
      .catch((err: unknown) => {
        const status =
          err && typeof err === "object" && "response" in err
            ? (err as { response?: { status?: number } }).response?.status
            : undefined;
        setError(
          status === 404
            ? "Product not found in backend. Go back and refresh the product list."
            : "Failed to load product",
        );
      })
      .finally(() => setLoadingProduct(false));
  }, [mode, id, form]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach((f) => formData.append("files", f));
      const { data } = await api.post("/upload/multiple", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImages((prev) => [
        ...prev,
        ...(data.files as UploadResponse[]).map((f) => ({
          url: f.url,
          filename: f.filename,
          mimetype: f.mimetype,
        })),
      ]);
    } catch {
      alert("Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const updateWeight = (
    i: number,
    field: keyof WeightOption,
    value: string,
  ) => {
    setWeightOptions((prev) =>
      prev.map((w, idx) => (idx === i ? { ...w, [field]: value } : w)),
    );
  };

  const onSubmit = async (values: FormValues) => {
    setSaving(true);
    setError("");
    try {
      const highlights = values.highlights
        ? values.highlights
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      const parsedWeights = weightOptions
        .filter((w) => w.weightValue && w.price)
        .map((w) => ({
          weightValue: parseFloat(w.weightValue),
          weightUnit: w.weightUnit,
          price: parseFloat(w.price),
          comparePrice: w.comparePrice ? parseFloat(w.comparePrice) : null,
        }));
      const payload = {
        ...values,
        highlights,
        images,
        weightOptions: parsedWeights,
      };

      if (mode === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${id}`, payload);
      navigate("/products");
    } catch (err: unknown) {
      const message =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : undefined;
      setError(message || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  if (loadingProduct)
    return (
      <div className="py-24 text-center text-stone-400">Loading product…</div>
    );

  return (
    <div className="space-y-6">
      <PageTitle
        title={mode === "new" ? "Create Product" : "Edit Product"}
        subtitle={
          mode === "new"
            ? "Add a new product to your catalog."
            : `Editing product #${id}`
        }
        action={
          <NavLink to="/products">
            <Button icon={ArrowLeft} variant="soft">
              Back to Products
            </Button>
          </NavLink>
        }
      />

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <FormCard
        title="Product Details"
        submitLabel={
          saving
            ? "Saving…"
            : mode === "new"
              ? "Create Product"
              : "Save Changes"
        }
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="grid gap-5 xl:grid-cols-[1fr_340px]">
          <div className="space-y-4">
            {/* Media Upload */}
            <div className="rounded-lg border border-dashed border-mango-orange/50 bg-mango-yellow/5 p-4">
              <div className="flex items-center gap-3">
                <Upload className="h-5 w-5 text-mango-orange shrink-0" />
                <p className="text-sm font-semibold text-leaf-dark flex-1">
                  Product Images / Videos
                </p>
                <label className="inline-flex min-h-9 cursor-pointer items-center justify-center rounded-lg bg-mango-yellow/30 px-4 text-sm font-semibold text-leaf-dark hover:bg-mango-yellow/50">
                  {uploading ? "Uploading…" : "Choose Files"}
                  <input
                    ref={fileInputRef}
                    accept="image/*,video/*"
                    className="sr-only"
                    multiple
                    type="file"
                    disabled={uploading}
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                </label>
              </div>
              {images.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {images.map((img, i) => (
                    <div key={img.url + i} className="relative">
                      {img.mimetype.startsWith("video/") ? (
                        <video
                          src={img.url}
                          className="h-20 w-20 rounded-lg object-cover bg-stone-100"
                        />
                      ) : (
                        <img
                          src={img.url}
                          alt="product"
                          className="h-20 w-20 rounded-lg object-cover"
                        />
                      )}
                      <button
                        type="button"
                        className="absolute -right-1.5 -top-1.5 rounded-full bg-red-600 p-0.5 text-white flex items-center justify-center"
                        onClick={() =>
                          setImages((prev) =>
                            prev.filter((_, idx) => idx !== i),
                          )
                        }
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Basic fields */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" {...form.register("name")} />
              <Field label="Subtitle" {...form.register("subtitle")} />
              <div className="sm:col-span-2">
                <Field
                  as="textarea"
                  label="Description"
                  {...form.register("description")}
                />
              </div>
              <Field label="Origin" {...form.register("origin")} />
              <Field label="Tags" {...form.register("tags")} />
              <Field
                label="Stock"
                type="number"
                {...form.register("stock", { valueAsNumber: true })}
              />
              <Field
                label="Low-Stock Threshold"
                type="number"
                {...form.register("threshold", { valueAsNumber: true })}
              />
              <div className="sm:col-span-2">
                <Field
                  as="textarea"
                  label="Highlights (one per line)"
                  placeholder="100% organic&#10;Hand sorted"
                  {...form.register("highlights")}
                />
              </div>
            </div>

            {/* Weight Options */}
            <div className="space-y-3">
              <p className="text-sm font-semibold text-leaf-dark">
                Weight Options
              </p>
              {weightOptions.map((w, i) => (
                <div
                  key={i}
                  className="flex items-end gap-2 rounded-lg border border-stone-200 bg-stone-50 p-3"
                >
                  {/* Weight Value + Unit */}
                  <div className="flex-1 min-w-0">
                    <label className="mb-1 block text-xs font-medium text-stone-600">
                      Weight
                    </label>
                    <div className="flex rounded-lg border border-stone-300 overflow-hidden bg-white">
                      <input
                        type="number"
                        placeholder="500"
                        value={w.weightValue}
                        onChange={(e) =>
                          updateWeight(i, "weightValue", e.target.value)
                        }
                        className="min-w-0 flex-1 px-3 py-2 text-sm outline-none"
                      />
                      <select
                        value={w.weightUnit}
                        onChange={(e) =>
                          updateWeight(i, "weightUnit", e.target.value)
                        }
                        className="border-l border-stone-300 bg-stone-50 px-2 py-2 text-sm outline-none"
                      >
                        {WEIGHT_UNITS.map((u) => (
                          <option key={u}>{u}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="mb-1 block text-xs font-medium text-stone-600">
                      Price ₹
                    </label>
                    <input
                      type="number"
                      placeholder="299"
                      value={w.price}
                      onChange={(e) => updateWeight(i, "price", e.target.value)}
                      className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-mango-orange"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="mb-1 block text-xs font-medium text-stone-600">
                      MRP ₹
                    </label>
                    <input
                      type="number"
                      placeholder="399"
                      value={w.comparePrice}
                      onChange={(e) =>
                        updateWeight(i, "comparePrice", e.target.value)
                      }
                      className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-mango-orange"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setWeightOptions((prev) =>
                        prev.filter((_, idx) => idx !== i),
                      )
                    }
                    className="rounded-lg p-2 text-red-500 hover:bg-red-50 disabled:opacity-30"
                    disabled={weightOptions.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setWeightOptions((prev) => [...prev, emptyWeight()])
                }
                className="flex items-center gap-2 text-sm font-semibold text-mango-orange hover:underline"
              >
                <Plus className="h-4 w-4" /> Add weight option
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-4 rounded-xl border border-mango-yellow/40 bg-mango-yellow/10 p-4">
            <Field as="select" label="Status" {...form.register("status")}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </Field>
          </aside>
        </div>
      </FormCard>
    </div>
  );
}
